import { EventEmitter } from "./EventEmitter";
import { Node } from "./Node";
import { Edge } from "./Edge";
import { NodeTypeRegistry } from "./NodeTypeRegistry";
import { CacheSystem } from "@cook-step/cache-system";
import { GraphQueries } from "./GraphQueries";
import {
  NodeId,
  EdgeId,
  SocketId,
  GraphEvents,
  GraphConfig,
  Node as INode,
  Edge as IEdge,
  SerializedGraph,
  BoundaryAnalysis,
  IHistoryManager,
} from "./types";

/**
 * Base Graph class - Pure graph data structure without business rules
 * This contains only structural operations and queries
 * DAG-specific validations and rules are in DAGGraph
 *
 * Uses composition over inheritance for event handling
 */
export class Graph {
  protected nodes: Map<NodeId, Node>;
  protected edges: Map<EdgeId, Edge>;
  protected registry: NodeTypeRegistry;

  // Event system via composition
  public readonly events: EventEmitter<GraphEvents>;

  // Optimized indices for O(1) lookups
  protected nodesByType: Map<string, Set<NodeId>>;
  protected edgesBySource: Map<NodeId, Map<SocketId, Set<EdgeId>>>;
  protected edgesByTarget: Map<NodeId, Map<SocketId, Set<EdgeId>>>;

  protected cache: CacheSystem<string, any>;
  protected graphQueries: GraphQueries;
  protected metadata: Record<string, unknown>;
  protected historyManager?: IHistoryManager;

  constructor(config?: GraphConfig) {
    // Initialize event system via composition - use provided or create new
    // Cast from IEventEmitter to EventEmitter (safe because EventEmitter implements IEventEmitter)
    this.events =
      (config?.eventEmitter as EventEmitter<GraphEvents>) ||
      new EventEmitter<GraphEvents>();

    this.nodes = new Map();
    this.edges = new Map();

    // Initialize or use provided registry
    this.registry = config?.registry || new NodeTypeRegistry();
    if (!config?.registry) {
      // Register common types if no registry provided
      this.registry.registerCommonTypes();
    }

    // Initialize indices
    this.nodesByType = new Map();
    this.edgesBySource = new Map();
    this.edgesByTarget = new Map();

    // Initialize cache system for expensive operations
    this.cache = new CacheSystem<string, any>({
      maxEntries: config?.cacheMaxEntries || 1000,
      enableImmutability: config?.cacheEnableImmutability ?? true,
      evictionPolicy: config?.cacheEvictionPolicy || "lru",
      defaultTTL: config?.cacheTTL, // undefined = no TTL by default
    });

    this.metadata = config?.metadata || {};
    this.historyManager = config?.historyManager;

    // Initialize query helper
    this.graphQueries = new GraphQueries(
      this.nodes,
      this.edges,
      this.nodesByType,
      this.edgesBySource,
      this.edgesByTarget,
    );
  }

  // ============================================
  // Basic Node Operations (Pure)
  // ============================================

  addNode(node: Node | INode): void {
    let nodeInstance: Node;

    if (node instanceof Node) {
      nodeInstance = node;
    } else {
      // Create new node
      nodeInstance = new Node({
        id: node.id,
        type: node.type,
        position: node.position,
        metadata: node.metadata,
        parameters: (node as any).parameters,
      });
    }

    // If node has a registered type and no sockets defined, populate from registry
    if (
      this.registry.hasType(nodeInstance.type) &&
      nodeInstance.inputSockets.length === 0 &&
      nodeInstance.outputSockets.length === 0
    ) {
      this.populateNodeSockets(nodeInstance);
    }

    this.nodes.set(nodeInstance.id, nodeInstance);

    // Update type index
    this.addToTypeIndex(nodeInstance);

    // Invalidate affected caches
    this.invalidateCache("topological-sort");

    // Record in history if manager exists
    if (this.historyManager) {
      this.historyManager.push({
        type: "nodeAdded",
        data: nodeInstance.toJSON(),
      });
    }

    this.events.emit("node:added", nodeInstance);
  }

  removeNode(nodeId: NodeId): boolean {
    const node = this.nodes.get(nodeId);
    if (!node) {
      return false;
    }

    // Remove all edges connected to this node
    const connectedEdges = this.graphQueries.getNodeEdges(nodeId);
    connectedEdges.forEach((edge) => {
      this.removeEdge(edge.id);
    });

    // Record in history if manager exists
    if (this.historyManager) {
      this.historyManager.push({
        type: "nodeRemoved",
        data: node.toJSON(),
      });
    }

    // Remove from type index
    this.removeFromTypeIndex(node);

    // Remove the node
    this.nodes.delete(nodeId);

    // Invalidate affected caches
    this.invalidateCache("topological-sort");

    // Emit with the removed node for undo/debug
    this.events.emit("node:removed", nodeId, node);
    return true;
  }

  updateNode(nodeId: NodeId, updates: Partial<INode>): boolean {
    const node = this.nodes.get(nodeId);
    if (!node) {
      return false;
    }

    // Store old state for history
    const oldNode = node.toJSON();
    const oldType = node.type;

    // Update node properties
    if (updates.position !== undefined) {
      node.position = updates.position;
    }
    if (updates.metadata !== undefined) {
      node.metadata = { ...node.metadata, ...updates.metadata };
    }
    if ((updates as any).parameters !== undefined) {
      (node as any).parameters = {
        ...(node as any).parameters,
        ...(updates as any).parameters,
      };
    }
    if (updates.type !== undefined) {
      node.type = updates.type;

      // Update type index if type changed
      if (updates.type !== oldType) {
        // Remove from old type index
        const typeSet = this.nodesByType.get(oldType);
        if (typeSet) {
          typeSet.delete(node.id);
          if (typeSet.size === 0) {
            this.nodesByType.delete(oldType);
          }
        }
        this.addToTypeIndex(node);
      }
    }

    // Record in history if manager exists
    if (this.historyManager) {
      this.historyManager.push({
        type: "nodeUpdated",
        data: { before: oldNode, after: node.toJSON() },
      });
    }

    this.events.emit("node:updated", nodeId, node);
    return true;
  }

  hasNode(nodeId: NodeId): boolean {
    return this.nodes.has(nodeId);
  }

  getNode(nodeId: NodeId): Node | undefined {
    return this.nodes.get(nodeId);
  }

  getNodes(): Node[] {
    return Array.from(this.nodes.values());
  }

  // ============================================
  // Basic Edge Operations (Pure)
  // ============================================

  /**
   * Check if a connection is structurally possible (not about validity)
   * This checks for impossible connections, not invalid ones.
   *
   * @returns true if connection is structurally possible (nodes/sockets exist, not duplicate)
   */
  canConnect(
    sourceNodeId: NodeId,
    sourceSocketId: SocketId,
    targetNodeId: NodeId,
    targetSocketId: SocketId,
  ): boolean {
    // Check if source node exists
    const sourceNode = this.nodes.get(sourceNodeId);
    if (!sourceNode) return false;

    // Check if target node exists
    const targetNode = this.nodes.get(targetNodeId);
    if (!targetNode) return false;

    // Check if source socket exists
    const sourceSocket = sourceNode.getOutputSocket(sourceSocketId);
    if (!sourceSocket) return false;

    // Check if target socket exists
    const targetSocket = targetNode.getInputSocket(targetSocketId);
    if (!targetSocket) return false;

    // Check for duplicate edge (same source and target)
    for (const edge of this.edges.values()) {
      if (
        edge.source.nodeId === sourceNodeId &&
        edge.source.socketId === sourceSocketId &&
        edge.target.nodeId === targetNodeId &&
        edge.target.socketId === targetSocketId
      ) {
        // Duplicate edge - structurally impossible
        return false;
      }
    }

    // Optional: Check for self-connection (uncomment if needed)
    // if (sourceNodeId === targetNodeId) {
    //   return false; // No self-connections
    // }

    // All structural checks passed
    return true;
  }

  addEdge(edge: Edge | IEdge): void {
    const edgeInstance = edge instanceof Edge ? edge : new Edge(edge);

    this.edges.set(edgeInstance.id, edgeInstance);

    // Update indices
    this.addToSourceIndex(edgeInstance);
    this.addToTargetIndex(edgeInstance);

    // Invalidate affected caches
    this.invalidateCache("topological-sort");

    // Record in history if manager exists
    if (this.historyManager) {
      this.historyManager.push({
        type: "edgeAdded",
        data: edgeInstance.toJSON(),
      });
    }

    this.events.emit("edge:added", edgeInstance);
  }

  removeEdge(edgeId: EdgeId): boolean {
    const edge = this.edges.get(edgeId);
    if (!edge) {
      return false;
    }

    // Record in history if manager exists
    if (this.historyManager) {
      this.historyManager.push({
        type: "edgeRemoved",
        data: edge.toJSON(),
      });
    }

    // Remove from indices
    this.removeFromSourceIndex(edge);
    this.removeFromTargetIndex(edge);

    // Remove the edge
    this.edges.delete(edgeId);

    // Invalidate affected caches
    this.invalidateCache("topological-sort");

    // Emit with the removed edge for undo/debug
    this.events.emit("edge:removed", edgeId, edge);
    return true;
  }

  hasEdge(edgeId: EdgeId): boolean {
    return this.edges.has(edgeId);
  }

  getEdge(edgeId: EdgeId): Edge | undefined {
    return this.edges.get(edgeId);
  }

  getEdges(): Edge[] {
    return Array.from(this.edges.values());
  }

  // ============================================
  // Graph Queries (Delegated)
  // ============================================

  getNodesByType(type: string): Node[] {
    return this.graphQueries.getNodesByType(type);
  }

  getNodeEdges(nodeId: NodeId): Edge[] {
    return this.graphQueries.getNodeEdges(nodeId);
  }

  getNodeInputEdges(nodeId: NodeId): Edge[] {
    return this.graphQueries.getNodeInputEdges(nodeId);
  }

  getNodeOutputEdges(nodeId: NodeId): Edge[] {
    return this.graphQueries.getNodeOutputEdges(nodeId);
  }

  getSocketEdges(nodeId: NodeId, socketId: SocketId): Edge[] {
    return this.graphQueries.getSocketEdges(nodeId, socketId);
  }

  getNodeDependencies(nodeId: NodeId): NodeId[] {
    return Array.from(this.graphQueries.getDependencies(nodeId));
  }

  getNodeDependents(nodeId: NodeId): NodeId[] {
    return Array.from(this.graphQueries.getDependents(nodeId));
  }

  getAncestors(nodeId: NodeId): Set<NodeId> {
    const ancestors = new Set<NodeId>();
    const visited = new Set<NodeId>();

    const traverse = (currentId: NodeId) => {
      if (visited.has(currentId)) return;
      visited.add(currentId);

      // Get all input edges for current node
      const inputEdges = this.getNodeInputEdges(currentId);

      for (const edge of inputEdges) {
        const sourceNodeId = edge.source.nodeId;
        if (!ancestors.has(sourceNodeId)) {
          ancestors.add(sourceNodeId);
          // Recursively get ancestors of the source node
          traverse(sourceNodeId);
        }
      }
    };

    traverse(nodeId);
    return ancestors;
  }

  getDescendants(nodeId: NodeId): Set<NodeId> {
    const descendants = new Set<NodeId>();
    const visited = new Set<NodeId>();

    const traverse = (currentId: NodeId) => {
      if (visited.has(currentId)) return;
      visited.add(currentId);

      // Get all output edges for current node
      const outputEdges = this.getNodeOutputEdges(currentId);

      for (const edge of outputEdges) {
        const targetNodeId = edge.target.nodeId;
        if (!descendants.has(targetNodeId)) {
          descendants.add(targetNodeId);
          // Recursively get descendants of the target node
          traverse(targetNodeId);
        }
      }
    };

    traverse(nodeId);
    return descendants;
  }

  // NOTE: DAG-specific methods moved to dag-graph package
  // getSourceNodes and getSinkNodes are now in DAGQueries service

  getInputNodes(): Node[] {
    return this.graphQueries.getInputNodes();
  }

  getOutputNodes(): Node[] {
    return this.graphQueries.getOutputNodes();
  }

  getBoundaryAnalysis(): BoundaryAnalysis {
    // For now, return basic analysis - DAGGraph will implement properly
    return {
      inputs: [],
      outputs: [],
    } as BoundaryAnalysis;
  }

  // ============================================
  // NOTE: DAG-specific methods moved to dag-graph package
  // ============================================
  // topologicalSort() is now in DAGQueries service
  // as it's specific to DAGs (general graphs may have cycles)

  // ============================================
  // Metadata Operations
  // ============================================

  getMetadata(): Record<string, unknown> {
    return { ...this.metadata };
  }

  setMetadata(metadata: Record<string, unknown>): void {
    this.metadata = { ...metadata };
  }

  updateMetadata(updates: Record<string, unknown>): void {
    this.metadata = { ...this.metadata, ...updates };
  }

  // ============================================
  // Serialization
  // ============================================

  serialize(): SerializedGraph {
    return {
      nodes: Array.from(this.nodes.values()).map((n) => n.toJSON()),
      edges: Array.from(this.edges.values()).map((e) => e.toJSON()),
      metadata: this.metadata,
    };
  }

  toJSON(): SerializedGraph {
    return this.serialize();
  }

  /**
   * Load a serialized graph into this instance, replacing current content
   * @param data - The serialized graph data
   */
  load(data: SerializedGraph): void {
    // Clear current graph
    this.clear();

    // Load metadata
    this.metadata = data.metadata || {};

    // Add nodes first
    data.nodes.forEach((nodeData) => {
      this.addNode(nodeData);
    });

    // Then add edges
    data.edges.forEach((edgeData) => {
      this.addEdge(edgeData);
    });
  }

  /**
   * Clear all nodes and edges from the graph
   */
  clear(): void {
    // Capture current state before clearing (useful for undo)
    const nodeCount = this.nodes.size;
    const edgeCount = this.edges.size;

    // Emit before clear event with counts
    this.events.emit("graph:beforeClear", { nodeCount, edgeCount });

    // Clear nodes and edges
    this.nodes.clear();
    this.edges.clear();

    // Clear indices
    this.nodesByType.clear();
    this.edgesBySource.clear();
    this.edgesByTarget.clear();

    // Clear cache
    this.cache.clear();

    // Clear metadata
    this.metadata = {};

    // Emit event with cleared counts for logging
    this.events.emit("graph:cleared", { nodeCount, edgeCount });
  }

  /**
   * Create a new Graph instance from serialized data
   * @param data - The serialized graph data
   * @returns A new Graph instance
   */
  static import(data: SerializedGraph): Graph {
    const graph = new Graph();
    graph.load(data);
    return graph;
  }

  // ============================================
  // Registry Access
  // ============================================

  getRegistry(): NodeTypeRegistry {
    return this.registry;
  }

  // ============================================
  // Helper Methods (Protected)
  // ============================================

  protected populateNodeSockets(node: Node): void {
    const typeDef = this.registry.getDefinition(node.type);
    if (!typeDef) return;

    // Add input sockets from type definition
    typeDef.inputs?.forEach((socketDef) => {
      node.addInputSocket({
        id: socketDef.id,
        schema: socketDef.schema || {},
        required: socketDef.required,
        multiple: socketDef.multiple,
      } as any);
    });

    // Add output sockets from type definition
    typeDef.outputs?.forEach((socketDef) => {
      node.addOutputSocket({
        id: socketDef.id,
        schema: socketDef.schema || {},
      } as any);
    });

    // Set default parameter values
    typeDef.parameters?.forEach((paramDef) => {
      if (paramDef.defaultValue !== undefined && node.parameters) {
        node.parameters[paramDef.id] = paramDef.defaultValue;
      }
    });
  }

  protected addToTypeIndex(node: Node): void {
    if (!this.nodesByType.has(node.type)) {
      this.nodesByType.set(node.type, new Set());
    }
    this.nodesByType.get(node.type)!.add(node.id);
  }

  protected removeFromTypeIndex(node: Node): void {
    const typeSet = this.nodesByType.get(node.type);
    if (typeSet) {
      typeSet.delete(node.id);
      if (typeSet.size === 0) {
        this.nodesByType.delete(node.type);
      }
    }
  }

  protected addToSourceIndex(edge: Edge): void {
    const nodeId = edge.source.nodeId;
    const socketId = edge.source.socketId;

    if (!this.edgesBySource.has(nodeId)) {
      this.edgesBySource.set(nodeId, new Map());
    }
    const nodeMap = this.edgesBySource.get(nodeId)!;

    if (!nodeMap.has(socketId)) {
      nodeMap.set(socketId, new Set());
    }
    nodeMap.get(socketId)!.add(edge.id);
  }

  protected removeFromSourceIndex(edge: Edge): void {
    const nodeMap = this.edgesBySource.get(edge.source.nodeId);
    if (nodeMap) {
      const socketSet = nodeMap.get(edge.source.socketId);
      if (socketSet) {
        socketSet.delete(edge.id);
        if (socketSet.size === 0) {
          nodeMap.delete(edge.source.socketId);
        }
      }
      if (nodeMap.size === 0) {
        this.edgesBySource.delete(edge.source.nodeId);
      }
    }
  }

  protected addToTargetIndex(edge: Edge): void {
    const nodeId = edge.target.nodeId;
    const socketId = edge.target.socketId;

    if (!this.edgesByTarget.has(nodeId)) {
      this.edgesByTarget.set(nodeId, new Map());
    }
    const nodeMap = this.edgesByTarget.get(nodeId)!;

    if (!nodeMap.has(socketId)) {
      nodeMap.set(socketId, new Set());
    }
    nodeMap.get(socketId)!.add(edge.id);
  }

  protected removeFromTargetIndex(edge: Edge): void {
    const nodeMap = this.edgesByTarget.get(edge.target.nodeId);
    if (nodeMap) {
      const socketSet = nodeMap.get(edge.target.socketId);
      if (socketSet) {
        socketSet.delete(edge.id);
        if (socketSet.size === 0) {
          nodeMap.delete(edge.target.socketId);
        }
      }
      if (nodeMap.size === 0) {
        this.edgesByTarget.delete(edge.target.nodeId);
      }
    }
  }

  protected invalidateCache(pattern?: string): void {
    if (pattern) {
      // Invalidate specific cache entries matching pattern
      const keys = Array.from(this.cache.keys());
      keys.forEach((key) => {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      });
    } else {
      // Clear entire cache
      this.cache.clear();
    }
  }

  // ============================================
  // Clone Support
  // ============================================

  clone(): Graph {
    const cloned = new Graph({
      metadata: { ...this.metadata },
      registry: this.registry,
    });

    // Clone nodes
    this.nodes.forEach((node) => {
      cloned.addNode(node);
    });

    // Clone edges
    this.edges.forEach((edge) => {
      cloned.addEdge(edge);
    });

    return cloned;
  }

  // ============================================
  // Proxy Methods for Backward Compatibility
  // ============================================
  // These will be deprecated in future versions
  // Users should migrate to graph.events.on/off/once/emit

  /**
   * @deprecated Use graph.events.on() instead
   */
  on<K extends keyof GraphEvents>(event: K, handler: GraphEvents[K]): void {
    this.events.on(event, handler);
  }

  /**
   * @deprecated Use graph.events.off() instead
   */
  off<K extends keyof GraphEvents>(event: K, handler?: GraphEvents[K]): void {
    this.events.off(event, handler);
  }

  /**
   * @deprecated Use graph.events.once() instead
   */
  once<K extends keyof GraphEvents>(event: K, handler: GraphEvents[K]): void {
    this.events.once(event, handler);
  }

  /**
   * @deprecated Use graph.events.emit() instead - This will be removed for security
   */
  protected emit<K extends keyof GraphEvents>(
    event: K,
    ...args: GraphEvents[K] extends (...args: infer P) => any ? P : never[]
  ): void {
    this.events.emit(event, ...args);
  }
}
